import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useServices, useDeployService } from '@/hooks/useServices'
import { usePendingDeploys, useApproveDeploy, useSkipDeploy, useRedeploy } from '@/hooks/useDeploys'
import { api } from '@/api/client'
import type { Service, DeployRecord } from '@/api/types'
import { Badge, Button, Card, Chip, EmptyState } from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'
import { CircleDot } from 'lucide-react'
import { serviceSourceURL } from '@/lib/imageSource'
import { GitHubIcon, DockerIcon } from '@/components/SourceIcons'

// Deploy flow status → Badge variant.
// pending=warning / approved=info / deploying=info / success=success / failed=destructive
const statusVariant: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'info',
  deploying: 'info',
  success: 'success',
  failed: 'destructive',
  skipped: 'muted',
  expired: 'muted',
}

function timeAgo(ts: number): string {
  const sec = Math.floor(Date.now() / 1000 - ts)
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

function duration(d: DeployRecord): string {
  if (!d.finished_at || !d.started_at) return '-'
  const sec = d.finished_at - d.started_at
  if (sec < 60) return `${sec}s`
  return `${Math.floor(sec / 60)}m ${sec % 60}s`
}

function firstLine(s: string | undefined): string {
  if (!s) return ''
  const i = s.indexOf('\n')
  return i < 0 ? s : s.slice(0, i)
}

// Rocket icon — used for the Manual Deploy action. Kept local since no
// other route needs it (GitHub/Docker icons live in components/SourceIcons).
function RocketIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={className} fill="currentColor">
      <path fillRule="evenodd" d="M14.064 0a8.75 8.75 0 00-6.187 2.563l-.459.458c-.314.314-.616.641-.904.979H3.31a1.75 1.75 0 00-1.49.833L.11 7.607a.75.75 0 00.418 1.11l3.102.954c.037.051.079.1.124.145l2.429 2.428c.046.046.094.088.145.125l.954 3.102a.75.75 0 001.11.418l2.774-1.707a1.75 1.75 0 00.833-1.49V9.485c.338-.288.665-.59.979-.904l.458-.459A8.75 8.75 0 0016 1.936V1.75A1.75 1.75 0 0014.25 0h-.186zM10.5 10.625c-.088.06-.177.118-.266.175l-2.35 1.521.548 1.783 1.949-1.2a.25.25 0 00.119-.213v-2.066zM3.678 8.116L5.2 5.766c.058-.09.117-.178.176-.266H3.309a.25.25 0 00-.213.119l-1.2 1.95 1.782.547zm5.26-4.493A7.25 7.25 0 0114.063 1.5h.186a.25.25 0 01.25.25v.186a7.25 7.25 0 01-2.123 5.127l-.459.458a15.21 15.21 0 01-2.499 2.02l-2.317 1.5-2.143-2.143 1.5-2.317a15.25 15.25 0 012.02-2.5l.458-.458h.002zM12 5a1 1 0 11-2 0 1 1 0 012 0z" />
    </svg>
  )
}

// Compact summary card for a service. Click to select — no inline history.
// The card root keeps a custom border/shadow treatment for selection state,
// so we use raw markup (not Card) here.
function ServiceCard({ svc, pending, selected, onSelect, onDeploy, deploying }: {
  svc: Service
  pending: DeployRecord[]
  selected: boolean
  onSelect: () => void
  onDeploy: () => void
  deploying: boolean
}) {
  const svcPending = pending.filter(p => p.service_id === svc.id)
  const hasPending = svcPending.length > 0
  const state = svc.container_state
  const stateLabel = state === 'not_found' ? 'not running' : state
  const dotColor =
    state === 'running' ? 'bg-success' :
    state === 'exited' ? 'bg-destructive' :
    'bg-muted-foreground/40'
  const src = serviceSourceURL(svc)

  return (
    <div
      onClick={onSelect}
      className={`group cursor-pointer w-full bg-card rounded-lg border transition-all overflow-hidden
        hover:shadow-md hover:-translate-y-0.5
        ${selected
          ? 'border-primary ring-2 ring-primary/15 shadow-md'
          : hasPending
            ? 'border-warning-soft'
            : 'border-border'}
      `}
    >
      {/* Header — left group has `flex-1 min-w-0` so the link truncates
          instead of pushing the action buttons off the right edge on
          mobile. `shrink-0` on each inner item that should never shrink
          (dot, badge, right group) keeps layout predictable. */}
      <div className="px-3.5 pt-3 pb-2 flex items-start gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${dotColor} ${state === 'running' ? 'shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : ''}`}
            title={stateLabel}
          />
          <Link
            to={`/services/${svc.id}`}
            onClick={e => e.stopPropagation()}
            className="font-semibold text-sm text-card-foreground hover:text-info truncate min-w-0"
          >{svc.name}</Link>
          {/* Status label is redundant on mobile (the colored dot already
              communicates state) — hide below sm to buy room for the
              action buttons on the right. */}
          <span className="hidden sm:inline text-[11px] text-muted-foreground capitalize truncate">{stateLabel}</span>
          {hasPending && (
            <Badge variant="warning" className="shrink-0 text-[10px] rounded font-semibold">
              {svcPending.length}
              <span className="hidden sm:inline"> PENDING</span>
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {src && (
            <a
              href={src.href}
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title={src.href}
              aria-label={src.kind === 'github' ? 'View on GitHub' : 'View on Docker Hub'}
            >
              {src.kind === 'github'
                ? <GitHubIcon className="w-3.5 h-3.5" />
                : <DockerIcon className="w-3.5 h-3.5" />
              }
            </a>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDeploy() }}
            disabled={deploying}
            className="p-1.5 text-info hover:text-info-foreground hover:bg-info rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Manual deploy — pull latest image and redeploy"
            aria-label="Manual deploy"
          >
            {deploying
              ? <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              : <RocketIcon className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-3.5 pb-3 space-y-1">
        <div className="font-mono text-[11px] text-muted-foreground truncate" title={svc.image}>
          {svc.image}
        </div>
        <div className="flex items-center gap-2 text-[11px] border-t border-border pt-1.5">
          {svc.last_deploy ? (
            <>
              <span className="font-mono text-foreground">{svc.last_deploy.commit_hash?.slice(0, 7) || '-'}</span>
              <span className="text-muted-foreground truncate flex-1" title={svc.last_deploy.commit_msg || ''}>
                {firstLine(svc.last_deploy.commit_msg) || <span className="italic text-muted-foreground">no message</span>}
              </span>
              <span className="text-muted-foreground shrink-0">{timeAgo(svc.last_deploy.started_at)}</span>
            </>
          ) : (
            <span className="text-muted-foreground italic">never deployed</span>
          )}
        </div>
      </div>
    </div>
  )
}

// Row in the bottom history panel. Click to expand deploy log + full commit msg.
// `redeployCount` = number of additional runs beyond this representative row.
// When > 0 we render a `↻ N` badge so the operator sees that the same build
// was deployed multiple times, instead of the list showing one row per run.
function HistoryRow({ d, redeployCount, current, expanded, onToggle, pending, approving, skipping, redeploying, onApprove, onSkip, onRedeploy }: {
  d: DeployRecord
  redeployCount: number
  current: boolean
  expanded: boolean
  onToggle: () => void
  pending: boolean
  approving: boolean
  skipping: boolean
  redeploying: boolean
  onApprove: () => void
  onSkip: () => void
  onRedeploy: () => void
}) {
  // Commit body = everything after the first line. GitHub commits conventionally
  // use "subject\n\nbody" — strip the blank-line separator for display.
  const msg = (d.commit_msg || '').trimEnd()
  const nl = msg.indexOf('\n')
  const title = nl < 0 ? msg : msg.slice(0, nl)
  const body = nl < 0 ? '' : msg.slice(nl + 1).replace(/^\n+/, '')

  return (
    <div className={current ? 'bg-success-soft/40' : ''}>
      <div
        className="px-3 sm:px-4 py-2.5 hover:bg-muted/50 cursor-pointer text-xs flex flex-col gap-1"
        onClick={onToggle}
      >
        {/* Primary row: status + commit + title + action buttons.
            Meta (duration / triggered-by / timeAgo) collapses to the
            secondary row on mobile so the action buttons stay in view
            regardless of how long the commit subject is. */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <span className="text-muted-foreground w-4 shrink-0">{expanded ? '▼' : '▶'}</span>
          {current && (
            <Badge
              variant="info"
              shape="square"
              className="shrink-0"
              title="Currently running"
              aria-label="Currently running"
            >
              <CircleDot size={12} aria-hidden="true" />
            </Badge>
          )}
          <Badge variant={statusVariant[d.status] ?? 'muted'} className="shrink-0 rounded">
            {d.status}
          </Badge>
          <span className="font-mono text-foreground shrink-0">{d.commit_hash?.slice(0, 7) || '-'}</span>
          {redeployCount > 0 && (
            <Badge
              variant="muted"
              className="shrink-0 text-[10px] rounded font-medium"
              title={`${redeployCount} additional run${redeployCount === 1 ? '' : 's'} of this commit`}
            >
              ↻ {redeployCount}
            </Badge>
          )}
          <span className="text-foreground truncate flex-1 min-w-0 basis-full sm:basis-auto order-last sm:order-none" title={msg}>
            {title || '-'}
          </span>
          {/* Desktop meta strip — right-aligned, fixed-width, as before */}
          <span className="hidden sm:inline text-muted-foreground w-12 text-right shrink-0">{duration(d)}</span>
          <span className="hidden sm:inline text-muted-foreground w-16 text-right shrink-0">{d.triggered_by}</span>
          <span className="hidden sm:inline text-muted-foreground w-20 text-right shrink-0">{timeAgo(d.started_at)}</span>
          {pending ? (
            <div className="flex items-center gap-1.5 ml-auto sm:ml-2 shrink-0">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onSkip() }}
                disabled={skipping}
                className="h-auto px-2 py-0.5 text-xs text-muted-foreground"
              >Skip</Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onApprove() }}
                disabled={approving}
                className="h-auto px-2 py-0.5 text-xs bg-success text-success-foreground hover:bg-success/90"
              >Deploy</Button>
            </div>
          ) : d.status !== 'deploying' && (
            <div className="flex items-center ml-auto sm:ml-2 shrink-0">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRedeploy() }}
                disabled={redeploying}
                title={
                  d.commit_hash
                    ? `Redeploy this commit — pulls :${d.commit_hash.slice(0, 7)} (falls back to :latest if the pinned tag wasn't pushed by CI).`
                    : 'Redeploy this entry — no commit recorded, so this pulls :latest.'
                }
                className="h-auto px-2 py-0.5 text-xs text-muted-foreground"
              >↻ Redeploy</Button>
            </div>
          )}
        </div>
        {/* Mobile-only meta strip: duration · triggered_by · timeAgo */}
        <div className="sm:hidden flex items-center gap-2 pl-6 text-[10px] text-muted-foreground">
          <span>{duration(d)}</span>
          <span className="opacity-40">·</span>
          <span>{d.triggered_by}</span>
          <span className="opacity-40">·</span>
          <span>{timeAgo(d.started_at)}</span>
        </div>
        {body && !expanded && (
          <div className="pl-6 sm:pl-[4.25rem] text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3" title={body}>
            {body}
          </div>
        )}
      </div>
      {expanded && (
        <div className="border-t border-border bg-muted/50">
          {d.commit_msg && (
            <div className="px-4 py-2 text-xs font-mono whitespace-pre-wrap text-foreground border-b border-border">
              <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">Commit message</div>
              {d.commit_msg}
            </div>
          )}
          {/* Terminal deploy log — KEPT RAW (CRT aesthetic) */}
          <div className="bg-black text-green-400 px-4 py-3 text-xs font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">
            <div className="text-gray-400 text-[10px] uppercase tracking-wide mb-1">Deploy log</div>
            {d.status === 'deploying' && !d.log && (
              <span className="text-yellow-400 animate-pulse">Deploying in progress...</span>
            )}
            {d.log || (d.status !== 'deploying' && <span className="text-gray-500">(no log recorded)</span>)}
          </div>
        </div>
      )}
    </div>
  )
}

// Bottom panel that shows the selected service's history.
function HistoryPanel({ svc, pending }: { svc: Service; pending: DeployRecord[] }) {
  const [logId, setLogId] = useState<number | null>(null)
  const approve = useApproveDeploy()
  const skip = useSkipDeploy()
  const redeploy = useRedeploy()

  const svcPending = pending.filter(p => p.service_id === svc.id)

  const { data: history } = useQuery({
    queryKey: ['service-deploys', svc.id],
    queryFn: () => api.listServiceDeploys(svc.id, 50),
    refetchInterval: 5000,
  })

  // CURRENT = most recently successful deploy row (by id, independent of
  // display order).
  const currentSuccessId = history
    ? history.reduce<number>(
        (acc, d) => (d.status === 'success' && d.id > acc ? d.id : acc),
        -1,
      )
    : (svc.last_deploy?.id ?? -1)

  // Collapse same-commit rows into a single representative row with a
  // redeploy counter. Empty commit_hash rows (legacy/manual without git)
  // stay individual so we don't merge unrelated deploys together. The
  // server already orders the list so the newest run of each commit
  // comes first within its group, letting us take the first occurrence
  // as the representative.
  const grouped: Array<{ rep: DeployRecord; count: number; members: DeployRecord[] }> = []
  if (history) {
    const keyIndex = new Map<string, number>()
    for (const d of history) {
      const key = d.commit_hash ? `c:${d.commit_hash}` : `i:${d.id}`
      const idx = keyIndex.get(key)
      if (idx === undefined) {
        keyIndex.set(key, grouped.length)
        grouped.push({ rep: d, count: 0, members: [d] })
      } else {
        const g = grouped[idx]
        g.count += 1
        g.members.push(d)
        // Keep rep pointing at whichever row has the highest id so the
        // representative always reflects the most recent run (matches
        // what CURRENT tracks).
        if (d.id > g.rep.id) {
          g.rep = d
        }
      }
    }
  }

  // currentGroupKey = the group id that contains the latest success row
  // so only one row lights up CURRENT even after consolidation.
  const currentRepId = (() => {
    if (currentSuccessId < 0) return -1
    const g = grouped.find((grp) => grp.members.some((m) => m.id === currentSuccessId))
    return g?.rep.id ?? currentSuccessId
  })()

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border bg-muted/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Deploy history:</span>
          <Link to={`/services/${svc.id}`} className="font-medium text-sm text-info hover:underline">
            {svc.name}
          </Link>
        </div>
        {svcPending.length > 0 && (
          <Badge variant="warning" className="text-xs rounded font-medium">
            {svcPending.length} awaiting approval
          </Badge>
        )}
      </div>
      <div className="divide-y divide-border">
        {!history && (
          <div className="px-4 py-6 text-center text-muted-foreground text-xs animate-pulse">Loading history...</div>
        )}
        {history?.length === 0 && (
          <div className="px-4 py-6 text-center text-muted-foreground text-xs">No deploy history</div>
        )}
        {grouped.map(({ rep, count }) => (
          <HistoryRow
            key={rep.id}
            d={rep}
            redeployCount={count}
            current={rep.id === currentRepId}
            expanded={logId === rep.id}
            onToggle={() => setLogId(logId === rep.id ? null : rep.id)}
            pending={rep.status === 'pending'}
            approving={approve.isPending}
            skipping={skip.isPending}
            redeploying={redeploy.isPending}
            onApprove={() => approve.mutate(rep.id)}
            onSkip={() => skip.mutate(rep.id)}
            onRedeploy={() => redeploy.mutate(rep.id)}
          />
        ))}
      </div>
    </Card>
  )
}

type Filter = 'all' | 'running' | 'exited' | 'not_found' | 'pending'

const filterLabels: Record<Filter, string> = {
  all: 'All',
  running: 'Running',
  exited: 'Exited',
  not_found: 'Not running',
  pending: 'Pending approval',
}

export default function Deploy() {
  const { data: services, isLoading: loadingSvc, error: svcError } = useServices()
  const { data: pending } = usePendingDeploys()
  const [filter, setFilter] = useState<Filter>('all')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const manual = useDeployService()

  const allPending = pending ?? []
  const allSvcs = services ?? []

  const counts: Record<Filter, number> = {
    all: allSvcs.length,
    running: allSvcs.filter(s => s.container_state === 'running').length,
    exited: allSvcs.filter(s => s.container_state === 'exited').length,
    not_found: allSvcs.filter(s => s.container_state === 'not_found').length,
    pending: allSvcs.filter(s => allPending.some(p => p.service_id === s.id)).length,
  }

  const deploySvcs = allSvcs.filter(svc => {
    switch (filter) {
      case 'running':   return svc.container_state === 'running'
      case 'exited':    return svc.container_state === 'exited'
      case 'not_found': return svc.container_state === 'not_found'
      case 'pending':   return allPending.some(p => p.service_id === svc.id)
      default:          return true
    }
  })

  const selected = allSvcs.find(s => s.id === selectedId) ?? null

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-foreground">Deploy</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {allPending.length > 0
            ? `${allPending.length} pending approval · Click a service to view deploy history`
            : 'Click a service to view deploy history'}
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(Object.keys(filterLabels) as Filter[]).map(f => (
          <Chip
            key={f}
            active={filter === f}
            onClick={() => setFilter(f)}
            count={counts[f]}
            tone={
              f === 'pending' && counts.pending > 0 && filter !== 'pending'
                ? 'warning'
                : 'default'
            }
          >
            {filterLabels[f]}
          </Chip>
        ))}
      </div>

      {svcError && (
        <div className="bg-destructive-soft text-destructive-soft-foreground px-4 py-3 rounded border border-destructive-soft text-sm">
          Failed to load services: {(svcError as Error).message}
        </div>
      )}

      {loadingSvc && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-20 bg-muted rounded-lg" />)}
        </div>
      )}

      {!loadingSvc && deploySvcs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
          {deploySvcs.map(svc => (
            <ServiceCard
              key={svc.id}
              svc={svc}
              pending={allPending}
              selected={svc.id === selectedId}
              onSelect={() => setSelectedId(svc.id === selectedId ? null : svc.id)}
              onDeploy={() => {
                if (confirm(`Manual deploy ${svc.name}? This will pull latest image and redeploy.`))
                  manual.mutate(svc.id)
              }}
              deploying={manual.isPending && manual.variables === svc.id}
            />
          ))}
        </div>
      )}

      {!loadingSvc && deploySvcs.length === 0 && (
        <Card>
          <EmptyState
            description={
              filter === 'all'
                ? 'No services registered yet. Add one from the Services page.'
                : `No services match filter "${filterLabels[filter]}".`
            }
          />
        </Card>
      )}

      {/* Bottom panel: history for the selected service */}
      {selected ? (
        <HistoryPanel svc={selected} pending={allPending} />
      ) : (
        <div className="border border-border rounded-lg bg-muted/50 px-4 py-6 text-center text-muted-foreground text-sm">
          Select a service above to see its deploy history.
        </div>
      )}
    </div>
  )
}
